import { NativeStackScreenProps } from '@react-navigation/native-stack';
import Constants from 'expo-constants';
import * as Updates from 'expo-updates';
import React, { useEffect, useRef, useState } from 'react';
import { ActivityIndicator, Platform, StyleSheet, Text, View } from 'react-native';
import logger from '../utils/logger';

type RootStackParamList = any;

interface SplashScreenProps extends NativeStackScreenProps<RootStackParamList, 'Splash'> {}

const SplashScreenComponent: React.FC<SplashScreenProps> = ({ navigation }) => {
  // Use static colors only - no context dependency
  const colors = {
    background: '#1a1a2e',
    primary: '#00d4ff',
    text: '#ffffff',
    textSecondary: '#999999',
  };

  const [message, setMessage] = useState('جاري التحقق من التحديثات...');
  const [updateProgress, setUpdateProgress] = useState(0);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;

    const checkAndApplyUpdates = async () => {
      try {
        logger.log('🔍 [SplashScreen] Starting update check...');

        // Skip in development and on web
        if (__DEV__ || Platform.OS === 'web') {
          logger.log('⏭️ [SplashScreen] Skipping updates (__DEV__ or web)');
          return;
        }

        if (isMountedRef.current) {
          setMessage('🔍 جاري فحص التحديثات...');
          setUpdateProgress(20);
        }

        // Check if updates are enabled
        if (!Updates.isEnabled) {
          logger.log('⏭️ [SplashScreen] Updates disabled');
          return;
        }

        // Check for available updates
        logger.log('📡 [SplashScreen] Checking for updates...');
        const update = await Updates.checkForUpdateAsync();
        if (isMountedRef.current) setUpdateProgress(40);

        if (update.isAvailable) {
          logger.log('✅ [SplashScreen] Update available, downloading...');
          if (isMountedRef.current) {
            setMessage('📥 جاري تحميل التحديث...');
            setUpdateProgress(50);
          }

          logger.log('📥 OTA Update available, downloading...', {
            updateId: update.manifest?.id,
            timestamp: new Date().toISOString(),
          });

          try {
            // Download the update
            await Updates.fetchUpdateAsync();
            if (isMountedRef.current) {
              setUpdateProgress(80);
              setMessage('✅ تم تحميل التحديث، جاري إعادة تشغيل...');
            }

            logger.log('✅ OTA update downloaded, reloading app...');

            // Give user visual feedback before reloading
            await new Promise((resolve) => setTimeout(resolve, 1000));

            // Apply the update immediately (reload app)
            if (isMountedRef.current) {
              setUpdateProgress(100);
              logger.log('🔄 [SplashScreen] Calling Updates.reloadAsync()...');
              await Updates.reloadAsync();
            }
            // Note: If reloadAsync succeeds, this code won't execute (app restarts)
          } catch (fetchError) {
            logger.error('❌ [SplashScreen] Error fetching update:', fetchError);
            logger.error('❌ Failed to fetch OTA update', fetchError);

            if (isMountedRef.current) {
              setMessage('تم تحميل التطبيق (بدون تحديث)');
            }
          }
        } else {
          // ✅ No updates available
          logger.log('✅ [SplashScreen] No updates available');
          if (isMountedRef.current) {
            setUpdateProgress(100);
            setMessage('✅ التطبيق محدث');
            logger.log('✅ No OTA updates available');
          }
        }
      } catch (error) {
        logger.error('⚠️ [SplashScreen] Error checking for updates:', error);
        logger.warn('⚠️ OTA update check failed', error);

        if (isMountedRef.current) {
          setMessage('تم تحميل التطبيق');
        }
      }
    };

    checkAndApplyUpdates();

    return () => {
      isMountedRef.current = false;
      logger.log('🧹 [SplashScreen] Cleanup: Component unmounting');
    };
  }, []);

  return (
    <View style={[styles.container, { backgroundColor: colors.background }]}>
      <View style={styles.content}>
        {/* Logo/Title */}
        <View style={styles.logoContainer}>
          <Text style={[styles.appName, { color: colors.primary }]}>IoT Edge</Text>
          <Text style={[styles.subtitle, { color: colors.text }]}>منصة المراقبة الذكية</Text>
        </View>

        {/* Loading indicator */}
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={colors.primary} style={styles.spinner} />
          <Text style={[styles.message, { color: colors.text }]} numberOfLines={2}>
            {message}
          </Text>
        </View>

        {/* Progress bar */}
        {updateProgress > 0 && (
          <View style={styles.progressContainer}>
            <View
              style={[
                styles.progressBar,
                {
                  width: `${updateProgress}%`,
                  backgroundColor: colors.primary,
                },
              ]}
            />
          </View>
        )}

        {/* Footer */}
        <View style={styles.footer}>
          <Text style={[styles.version, { color: colors.textSecondary }]}>
            v{Constants.expoConfig?.version || '2.3.32'}
          </Text>
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  content: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
  },
  logoContainer: {
    marginBottom: 60,
    alignItems: 'center',
  },
  appName: {
    fontSize: 48,
    fontWeight: 'bold',
    marginBottom: 8,
  },
  subtitle: {
    fontSize: 14,
    fontWeight: '500',
  },
  loadingContainer: {
    alignItems: 'center',
    marginVertical: 40,
  },
  spinner: {
    marginBottom: 20,
  },
  message: {
    fontSize: 14,
    fontWeight: '500',
    textAlign: 'center',
    maxWidth: 250,
  },
  progressContainer: {
    width: '100%',
    maxWidth: 200,
    height: 6,
    backgroundColor: '#e0e0e0',
    borderRadius: 3,
    overflow: 'hidden',
    marginTop: 20,
  },
  progressBar: {
    height: '100%',
    borderRadius: 3,
  },
  footer: {
    position: 'absolute',
    bottom: 30,
  },
  version: {
    fontSize: 12,
    fontWeight: '400',
  },
});

export default SplashScreenComponent;
